import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { Spinner } from '../../components/ui/Spinner.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { salesApi } from '../../services/salesApi.js';
import { formatCurrency, formatDate, formatNumber } from '../../utils/formatters.js';
import { downloadCreditNotePdf, previewCreditNotePdf } from '../../utils/pdf.js';

/* Kein Rückfall auf einen fremden Absender.
 *
 * Hier standen fest verdrahtet Recicas Name und Recicas E-Mail. Solange es
 * eine Installation gab, war das ein Platzhalter. Mit mehreren Kundenfirmen
 * wird daraus ein Fehler mit Aussenwirkung: eine Firma, die ihre Angaben noch
 * nicht ausgefüllt hat, verschickt ihre Rechnung mit dem Briefkopf und der
 * Kontaktadresse eines fremden Unternehmens.
 *
 * Leer ist die ehrlichere Antwort. Wer den eigenen Namen auf der Rechnung
 * vermisst, trägt ihn in den Einstellungen nach. */

export function CreditNotePrintPage() {
  const { t } = useTranslation();
  const { id } = useParams();

  const creditNote = useQuery({
    queryKey: ['finance', 'credit-notes', id],
    queryFn: () => financeApi.getCreditNote(id).then((r) => r.items),
  });
  const settings = useQuery({
    queryKey: ['finance', 'company-settings'],
    queryFn: () => financeApi.getCompanySettings().then((r) => r.items),
  });
  const cn = creditNote.data;
  const invoice = useQuery({
    queryKey: ['finance', 'invoices', cn?.invoiceId],
    queryFn: () => financeApi.getInvoice(cn.invoiceId).then((r) => r.items),
    enabled: Boolean(cn?.invoiceId),
  });
  const customer = useQuery({
    queryKey: ['sales', 'customer', cn?.customerId],
    queryFn: () => salesApi.getCustomer(cn.customerId).then((r) => r.items),
    enabled: Boolean(cn?.customerId),
  });
  const suppliers = useQuery({
    queryKey: ['inventory', 'suppliers'],
    queryFn: () => inventoryApi.listSuppliers().then((r) => r.items || []),
    enabled: Boolean(cn?.vendorId),
  });

  if (creditNote.isLoading) {
    return <section className="page"><Spinner /></section>;
  }
  if (creditNote.error || !cn) {
    return (
      <section className="page">
        <div className="alert danger">{creditNote.error?.message || 'Not found'}</div>
        <Link to="/finance/credit-notes" className="btn">← {t('creditNote.title')}</Link>
      </section>
    );
  }

  const cfg = settings.data || {};
  const party = cn.vendorId
    ? (suppliers.data || []).find((s) => s.id === cn.vendorId)
    : customer.data;

  return (
    <div className="invoice-print-wrap">
      <div className="invoice-toolbar no-print">
        <Link to="/finance/credit-notes" className="btn">← {t('common.back')}</Link>
        <div className="invoice-toolbar-actions">
          <button type="button" className="btn" onClick={() => previewCreditNotePdf({ creditNote: cn, invoice: invoice.data, customer: party, settings: settings.data, t })}>
            {t('pdf.preview')}
          </button>
          <button type="button" className="btn"
            onClick={() => downloadCreditNotePdf({ creditNote: cn, invoice: invoice.data, customer: party, settings: settings.data, t })}>
            {t('invoice.print.downloadPdf')}
          </button>
          <button type="button" className="btn primary" onClick={() => window.print()}>
            {t('invoice.print.printOrSave')}
          </button>
        </div>
      </div>

      <article className="invoice-doc">
        <header className="invoice-doc-head">
          <div className="invoice-doc-brand">
            <div className="invoice-doc-brand-name">{cfg.companyName}</div>
            {cfg.addressLine1 && <div className="invoice-doc-muted">{cfg.addressLine1}</div>}
            {(cfg.zip || cfg.city) && <div className="invoice-doc-muted">{[cfg.zip, cfg.city].filter(Boolean).join(' ')}</div>}
            <div className="invoice-doc-muted">{cfg.email}</div>
          </div>
          <div className="invoice-doc-title">
            <h1>{t(cn.type === 'PURCHASE' ? 'creditNote.print.headingVendor' : 'creditNote.print.heading')}</h1>
            <div className="invoice-doc-number">{cn.creditNoteNumber}</div>
          </div>
        </header>

        <section className="invoice-doc-parties">
          <div>
            <div className="invoice-doc-label">{t('invoice.print.billTo')}</div>
            <div className="invoice-doc-party-name">{party?.name || (cn.vendorId || cn.customerId || '').slice(0, 8)}</div>
            {party?.email && <div className="invoice-doc-muted">{party.email}</div>}
          </div>
          <div className="invoice-doc-meta">
            <div><span>{t('col.date')}</span><strong>{formatDate(cn.creditDate)}</strong></div>
            <div><span>{t('creditNote.print.forInvoice')}</span><strong>{invoice.data?.invoiceNumber || '-'}</strong></div>
          </div>
        </section>

        <table className="invoice-doc-table">
          <thead>
            <tr>
              <th>{t('detail.lineDescription')}</th>
              <th className="num">{t('detail.lineQty')}</th>
              <th className="num">{t('detail.lineUnitPrice')}</th>
              <th className="num">{t('vat.rateCol')}</th>
              <th className="num">{t('detail.lineTotal')}</th>
            </tr>
          </thead>
          <tbody>
            {(cn.lines || []).map((r) => (
              <tr key={r.id}>
                <td>{r.description}</td>
                <td className="num">{formatNumber(r.quantity)}</td>
                <td className="num">{formatCurrency(r.unitPrice, cn.currency)}</td>
                <td className="num">{Number(r.taxRate || 0).toFixed(1)} %</td>
                <td className="num">{formatCurrency(r.lineAmount, cn.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <section className="invoice-doc-totals">
          <div className="invoice-doc-totals-box">
            <div><span>{t('vat.net')}</span><span>{formatCurrency(cn.netAmount ?? cn.totalAmount, cn.currency)}</span></div>
            <div><span>{t('vat.tax')}</span><span>{formatCurrency(cn.taxAmount ?? 0, cn.currency)}</span></div>
            <div className="invoice-doc-due"><span>{t('creditNote.print.total')}</span><strong>{formatCurrency(cn.totalAmount, cn.currency)}</strong></div>
          </div>
        </section>

        <footer className="invoice-doc-footer">{[cfg.companyName, cfg.email].filter(Boolean).join(' · ')}</footer>
      </article>
    </div>
  );
}
